import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { MaskedInput } from '../../components/ui/masked-input';
import { Button } from '../../components/ui/button';
import { Autocomplete } from '../../components/ui/autocomplete';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import TableComponent from '../../components/TableComponent';
import Drawer from '../../components/Drawer';
import ConfirmDialog from '../../components/ConfirmDialog';
import ClientQuickCreateModal from '../../components/ClientQuickCreateModal';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { clientsService, salesService, stockAvailabilityService } from '../../services/salesService';
import { productsService, warehousesService } from '../../services/productionService';
import { formatBRL, formatDateBR, formatDateTimeBR, formatQuantity, localDateISO, parseQuantityInput } from '../../lib/utils';

const ORIGIN_OPTIONS = [
    { value: 'internal', label: 'Interna' },
    { value: 'external', label: 'Externa' },
];

const STATUS_LABELS = {
    draft: 'Orçado',
    approved: 'Aprovado',
    partial: 'Parcial',
    completed: 'Concluído',
    cancelled: 'Cancelado',
    expired: 'Expirado',
};

function emptyItemForm() {
    return {
        product_id: null,
        warehouse_id: null,
        quantity: '',
        unit_price: '',
    };
}

function formatAddress(place) {
    const street = [place.street, place.number].filter(Boolean).join(', ');
    const city = [place.city, place.state].filter(Boolean).join('/');
    return [street, place.complement, place.neighborhood, city].filter(Boolean).join(' - ');
}

function SaleForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;
    const { toast, toastApiError } = useToast();
    const { user, can } = useAuth();
    const canSales = can('Sales');

    usePageTitle(isEditing ? 'Pedido' : 'Novo pedido');

    const [sale, setSale] = useState(null);
    const [clients, setClients] = useState([]);
    const [products, setProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [users, setUsers] = useState([]);

    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);
    const [clientModalOpen, setClientModalOpen] = useState(false);
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [finishDialogOpen, setFinishDialogOpen] = useState(false);
    const [nextNumberPreview, setNextNumberPreview] = useState(null);

    const [clientId, setClientId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [origin, setOrigin] = useState('');
    const [customerPurchaseOrder, setCustomerPurchaseOrder] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [details, setDetails] = useState('');
    const [items, setItems] = useState([]);

    const [itemDrawerOpen, setItemDrawerOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemFormData, setItemFormData] = useState(emptyItemForm());
    const [itemAvailability, setItemAvailability] = useState(null);
    const [itemAvailabilityLoading, setItemAvailabilityLoading] = useState(false);
    const [deleteItemDialog, setDeleteItemDialog] = useState({ open: false, id: null });

    const [activeTab, setActiveTab] = useState('general');

    const [deliveryDrawerOpen, setDeliveryDrawerOpen] = useState(false);
    const [deliveryDate, setDeliveryDate] = useState(localDateISO());
    const [deliveryNotes, setDeliveryNotes] = useState('');
    const [deliveryLocation, setDeliveryLocation] = useState('');
    const [deliveryInvoice, setDeliveryInvoice] = useState('');
    const [deliveryQuantities, setDeliveryQuantities] = useState({});
    const [deliverySaving, setDeliverySaving] = useState(false);

    const isDraft = !isEditing || sale?.status === 'draft';
    const isOpenForDelivery = isEditing && ['approved', 'partial'].includes(sale?.status);
    // Pedido segue editável até ser concluído/cancelado, como nas ordens de produção.
    const canEdit = !isEditing || ['draft', 'approved', 'partial'].includes(sale?.status);

    useEffect(() => {
        (async () => {
            try {
                const [clientsRes, productsRes, warehousesRes, usersRes] = await Promise.all([
                    clientsService.list({ per_page: 1000, status: 'active' }),
                    productsService.list({ per_page: 2000, status: 'active' }),
                    warehousesService.list({ per_page: 1000 }),
                    api.get('/users', { params: { per_page: 1000, module: 'Sales' } }),
                ]);
                setClients(clientsRes.data.data || clientsRes.data || []);
                setProducts(productsRes.data.data || productsRes.data || []);
                setWarehouses(warehousesRes.data.data || warehousesRes.data || []);
                setUsers(usersRes.data.data || usersRes.data || []);
            } catch (e) {
                console.error('Error loading form catalogs:', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isEditing) {
            loadSale();
        } else {
            setUserId(user?.id ?? null);
            setLoading(false);
            salesService.nextNumber()
                .then((res) => setNextNumberPreview(res.data?.next_document_number))
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEditing]);

    const loadSale = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const response = await salesService.get(id);
            const data = response.data?.data || response.data;
            setSale(data);
            setClientId(data.client_id);
            setUserId(data.user_id);
            // Mantém o vendedor já gravado na lista mesmo que ele não tenha mais
            // permissão no módulo de pedidos.
            if (data.user) {
                setUsers((prev) => (prev.some((u) => u.id === data.user.id) ? prev : [...prev, data.user]));
            }
            setOrigin(data.origin || '');
            setCustomerPurchaseOrder(data.customer_purchase_order || '');
            setPaymentMethod(data.payment_method || '');
            setDetails(data.details || '');
            setItems((data.items || []).map((it) => ({
                id: it.id,
                product_id: it.product_id,
                warehouse_id: it.warehouse_id,
                quantity: String(it.quantity ?? ''),
                unit_price: String(it.unit_price ?? ''),
                delivered_quantity: it.delivered_quantity,
                remaining_quantity: it.remaining_quantity,
                available_stock: it.available_stock,
            })));
        } catch (e) {
            console.error('Error loading sale:', e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar o pedido' });
            navigate('/sales');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const productById = useMemo(() => {
        const map = new Map();
        products.forEach((p) => map.set(p.id, p));
        return map;
    }, [products]);

    const warehouseById = useMemo(() => {
        const map = new Map();
        warehouses.forEach((w) => map.set(w.id, w));
        return map;
    }, [warehouses]);

    const itemColumns = useMemo(() => {
        const base = [
            {
                key: 'product_id',
                label: 'Produto',
                render: (value) => {
                    const product = productById.get(value);
                    return product ? `${product.code} - ${product.description}` : '-';
                },
            },
            {
                key: 'warehouse_id',
                label: 'Depósito',
                render: (value) => warehouseById.get(value)?.name || '-',
            },
            {
                key: 'quantity',
                label: 'Quantidade',
                render: (value) => formatQuantity(parseQuantityInput(value) || 0),
            },
        ];

        if (!isDraft) {
            base.push(
                {
                    key: 'delivered_quantity',
                    label: 'Entregue',
                    render: (value) => formatQuantity(value ?? 0),
                },
                {
                    key: 'remaining_quantity',
                    label: 'Restante',
                    render: (value) => formatQuantity(value ?? 0),
                },
            );
        }

        base.push(
            {
                key: 'unit_price',
                label: 'Valor unit.',
                render: (value) => formatBRL(parseFloat(value) || 0),
            },
            {
                key: 'total',
                label: 'Total',
                render: (value, row) => formatBRL((parseQuantityInput(row.quantity) || 0) * (parseFloat(row.unit_price) || 0)),
            },
        );

        return base;
    }, [productById, warehouseById, isDraft]);

    const deliverableItems = useMemo(() => (
        (sale?.items || [])
            .map((it) => ({
                ...it,
                remaining: it.remaining_quantity ?? (parseFloat(it.quantity) - parseFloat(it.delivered_quantity || 0)),
            }))
            .filter((it) => it.remaining > 0)
    ), [sale]);

    const deliveryLocationOptions = useMemo(() => {
        const client = sale?.client;
        if (!client) return [];
        const options = [];
        if (client.street || client.city) {
            options.push({ value: 'client', label: `Endereço do cliente - ${formatAddress(client)}` });
        }
        (client.sites || []).forEach((site) => {
            options.push({ value: `site:${site.id}`, label: `Obra: ${site.name} - ${formatAddress(site)}` });
        });
        return options;
    }, [sale?.client]);

    const deliveryColumns = useMemo(() => ([
        {
            key: 'id',
            label: 'Atendimento',
            className: 'font-medium tabular-nums',
        },
        {
            key: 'invoice_number',
            label: 'NF',
            className: 'tabular-nums',
            render: (value) => value || '-',
        },
        {
            key: 'delivery_date',
            label: 'Data',
            render: (value) => formatDateBR(value),
        },
        {
            key: 'items',
            label: 'Itens entregues',
            render: (value, row) => (
                <ul className="space-y-1">
                    {(row.items || []).map((di) => {
                        const saleItem = items.find((it) => it.id === di.sale_item_id);
                        const product = saleItem ? productById.get(saleItem.product_id) : null;
                        return (
                            <li key={di.id} className="flex items-baseline gap-2">
                                <span className="font-medium text-gray-900 tabular-nums whitespace-nowrap">{formatQuantity(di.quantity)}x</span>
                                <span className="text-gray-700">{product ? `${product.code} - ${product.description}` : `Item #${di.sale_item_id}`}</span>
                            </li>
                        );
                    })}
                </ul>
            ),
        },
        {
            key: 'delivery_location',
            label: 'Local de entrega',
            render: (value, row) => (value ? (
                <div>
                    <p className="font-medium text-gray-900">{value}</p>
                    {row.delivery_address && <p className="text-xs text-gray-500">{row.delivery_address}</p>}
                </div>
            ) : '-'),
        },
        {
            key: 'notes',
            label: 'Observações',
            render: (value) => value || '-',
        },
        {
            key: 'user',
            label: 'Usuário',
            render: (value, row) => row.user?.name || '-',
        },
        {
            key: 'pdf',
            label: 'PDF',
            icon: 'pdf',
            render: (value, row) => (
                <Button type="button" variant="outline" onClick={() => handleDeliveryPdf(row.id)}>
                    Gerar PDF
                </Button>
            ),
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ]), [items, productById, sale?.document_number]);

    const refreshItemAvailability = async (productId, warehouseId) => {
        if (!productId || !warehouseId) {
            setItemAvailability(null);
            return;
        }
        setItemAvailabilityLoading(true);
        try {
            const response = await stockAvailabilityService.get(productId, warehouseId);
            setItemAvailability(response.data?.available ?? null);
        } catch (e) {
            setItemAvailability(null);
        } finally {
            setItemAvailabilityLoading(false);
        }
    };

    const openItemDrawerForNew = () => {
        setEditingItem(null);
        setItemFormData(emptyItemForm());
        setItemAvailability(null);
        setItemDrawerOpen(true);
    };

    const openItemDrawerForEdit = (item) => {
        setEditingItem(item);
        setItemFormData({
            product_id: item.product_id,
            warehouse_id: item.warehouse_id,
            quantity: String(item.quantity ?? ''),
            unit_price: String(item.unit_price ?? ''),
        });
        setItemAvailability(item.available_stock ?? null);
        setItemDrawerOpen(true);
        refreshItemAvailability(item.product_id, item.warehouse_id);
    };

    const handleItemProductSelect = (productId) => {
        const product = productById.get(productId);
        setItemFormData((prev) => {
            const warehouseId = prev.warehouse_id ?? product?.default_warehouse_id ?? null;
            return {
                ...prev,
                product_id: productId,
                warehouse_id: warehouseId,
                unit_price: prev.unit_price || (product?.sale_price != null ? String(product.sale_price) : ''),
            };
        });
        const warehouseId = itemFormData.warehouse_id ?? product?.default_warehouse_id ?? null;
        refreshItemAvailability(productId, warehouseId);
    };

    const handleItemWarehouseSelect = (e) => {
        const warehouseId = e.target.value ? Number(e.target.value) : null;
        setItemFormData((prev) => ({ ...prev, warehouse_id: warehouseId }));
        refreshItemAvailability(itemFormData.product_id, warehouseId);
    };

    const handleItemFormChange = (e) => {
        setItemFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleItemSave = () => {
        if (!itemFormData.product_id) {
            toast({ title: 'Produto obrigatório', description: 'Selecione um produto.', variant: 'error' });
            return;
        }
        if (!itemFormData.warehouse_id) {
            toast({ title: 'Depósito obrigatório', description: 'Selecione um depósito.', variant: 'error' });
            return;
        }
        const qty = parseQuantityInput(itemFormData.quantity);
        if (!Number.isFinite(qty) || qty <= 0) {
            toast({ title: 'Quantidade inválida', description: 'Informe uma quantidade maior que zero.', variant: 'error' });
            return;
        }

        const row = {
            // Itens ainda não salvos ganham id textual para não serem confundidos
            // com o id real do backend ao montar o payload.
            id: editingItem?.id ?? `new-${Date.now()}`,
            product_id: itemFormData.product_id,
            warehouse_id: itemFormData.warehouse_id,
            quantity: itemFormData.quantity,
            unit_price: itemFormData.unit_price,
            available_stock: itemAvailability,
            delivered_quantity: editingItem?.delivered_quantity ?? 0,
        };

        setItems((prev) => (editingItem
            ? prev.map((it) => (it.id === editingItem.id ? row : it))
            : [...prev, row]));
        setItemDrawerOpen(false);
    };

    const handleRemoveItemConfirm = () => {
        setItems((prev) => prev.filter((it) => it.id !== deleteItemDialog.id));
        setDeleteItemDialog({ open: false, id: null });
    };

    const total = useMemo(() => {
        return items.reduce((sum, it) => {
            const qty = parseQuantityInput(it.quantity) || 0;
            const price = parseFloat(it.unit_price) || 0;
            return sum + qty * price;
        }, 0);
    }, [items]);

    const buildPayload = () => ({
        client_id: clientId,
        user_id: userId,
        origin: origin || null,
        customer_purchase_order: customerPurchaseOrder || null,
        payment_method: paymentMethod || null,
        details: details || null,
        items: items
            .filter((it) => it.product_id)
            .map((it) => ({
                // Item existente mantém o id para preservar o que já foi atendido.
                id: typeof it.id === 'number' ? it.id : undefined,
                product_id: it.product_id,
                warehouse_id: it.warehouse_id,
                quantity: parseQuantityInput(it.quantity),
                unit_price: it.unit_price !== '' ? parseFloat(it.unit_price) : undefined,
            })),
    });

    const handleSave = async () => {
        if (!clientId) {
            toast({ title: 'Cliente obrigatório', description: 'Selecione um cliente.', variant: 'error' });
            return;
        }

        setFormLoading(true);
        try {
            const payload = buildPayload();
            if (isEditing) {
                const response = await salesService.update(id, payload);
                const saved = response.data?.data || response.data;
                setSale(saved);
                toast({ title: 'Pedido salvo', description: 'Alterações salvas com sucesso', variant: 'success' });
            } else {
                const response = await salesService.create(payload);
                const saved = response.data?.data || response.data;
                toast({ title: 'Pedido criado', description: `Nº ${saved.document_number}`, variant: 'success' });
                navigate(`/sales/${saved.id}`);
            }
        } catch (e) {
            console.error('Error saving sale:', e);
            toastApiError(e, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar o pedido' });
        } finally {
            setFormLoading(false);
        }
    };

    const handleApprove = async () => {
        try {
            await salesService.approve(id);
            setApproveDialogOpen(false);
            await loadSale();
            toast({ title: 'Pedido aprovado', description: 'Já pode registrar atendimentos (entregas)', variant: 'success' });
        } catch (e) {
            console.error('Error approving sale:', e);
            toastApiError(e, { title: 'Erro ao aprovar', fallback: 'Não foi possível aprovar o pedido' });
        }
    };

    const handleCancel = async () => {
        try {
            await salesService.cancel(id);
            setCancelDialogOpen(false);
            await loadSale();
            toast({ title: 'Pedido cancelado', variant: 'success' });
        } catch (e) {
            console.error('Error cancelling sale:', e);
            toastApiError(e, { title: 'Erro ao cancelar', fallback: 'Não foi possível cancelar' });
        }
    };

    const handleFinish = async () => {
        try {
            await salesService.finish(id);
            setFinishDialogOpen(false);
            await loadSale();
            toast({ title: 'Pedido finalizado', description: 'O saldo pendente foi encerrado', variant: 'success' });
        } catch (e) {
            console.error('Error finishing sale:', e);
            toastApiError(e, { title: 'Erro ao finalizar', fallback: 'Não foi possível finalizar o pedido' });
        }
    };

    const handleDeliveryPdf = async (deliveryId) => {
        try {
            await salesService.downloadDeliveryPdf(id, deliveryId, sale?.document_number);
        } catch (e) {
            toastApiError(e, { title: 'Erro ao gerar PDF', fallback: 'Não foi possível gerar o PDF do atendimento' });
        }
    };

    const openDeliveryDrawer = () => {
        const initial = {};
        (sale?.items || []).forEach((it) => {
            initial[it.id] = '';
        });
        setDeliveryQuantities(initial);
        setDeliveryDate(localDateISO());
        setDeliveryNotes('');
        setDeliveryInvoice('');
        const hasClientAddress = deliveryLocationOptions.some((option) => option.value === 'client');
        if (hasClientAddress) {
            setDeliveryLocation('client');
        } else {
            setDeliveryLocation(deliveryLocationOptions.length === 1 ? deliveryLocationOptions[0].value : '');
        }
        setDeliveryDrawerOpen(true);
    };

    const handleDeliveryQuantityChange = (itemId, value) => {
        setDeliveryQuantities((prev) => ({ ...prev, [itemId]: value }));
    };

    const handleDeliverySave = async () => {
        const lines = Object.entries(deliveryQuantities)
            .map(([saleItemId, value]) => ({ sale_item_id: Number(saleItemId), quantity: parseQuantityInput(value) }))
            .filter((line) => Number.isFinite(line.quantity) && line.quantity > 0);

        if (lines.length === 0) {
            toast({ title: 'Informe ao menos uma quantidade', variant: 'error' });
            return;
        }

        if (!deliveryLocation) {
            toast({ title: 'Local de entrega obrigatório', description: 'Selecione o endereço do cliente ou uma obra.', variant: 'error' });
            return;
        }

        setDeliverySaving(true);
        try {
            await salesService.createDelivery(id, {
                delivery_date: deliveryDate,
                invoice_number: deliveryInvoice.trim() || null,
                location_type: deliveryLocation === 'client' ? 'client' : 'site',
                client_site_id: deliveryLocation === 'client' ? null : Number(deliveryLocation.split(':')[1]),
                notes: deliveryNotes || null,
                items: lines,
            });
            setDeliveryQuantities({});
            setDeliveryNotes('');
            await loadSale({ silent: true });
            toast({ title: 'Atendimento registrado', description: 'Estoque baixado com sucesso', variant: 'success' });
        } catch (e) {
            console.error('Error creating delivery:', e);
            toastApiError(e, { title: 'Erro ao registrar', fallback: 'Não foi possível registrar o atendimento' });
        } finally {
            setDeliverySaving(false);
        }
    };

    const totalOrderedQty = useMemo(
        () => items.reduce((sum, it) => sum + (parseQuantityInput(it.quantity) || 0), 0),
        [items]
    );
    const totalDeliveredQty = useMemo(
        () => items.reduce((sum, it) => sum + (parseFloat(it.delivered_quantity) || 0), 0),
        [items]
    );
    const deliveryProgress = totalOrderedQty > 0 ? (totalDeliveredQty / totalOrderedQty) * 100 : 0;

    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title={isEditing ? `Pedido Nº ${sale?.document_number ?? ''}` : `Novo pedido${nextNumberPreview ? ` (Nº ${nextNumberPreview})` : ''}`}
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Vendas' },
                        { label: 'Pedidos', path: '/sales' },
                    ]}
                    additionalActions={[
                        ...(isEditing ? [{
                            label: 'Gerar PDF',
                            icon: 'pdf',
                            variant: 'outline',
                            onClick: async () => {
                                try {
                                    await salesService.downloadPdf(id, sale?.document_number);
                                } catch (e) {
                                    toastApiError(e, { title: 'Erro ao gerar PDF', fallback: 'Não foi possível gerar o PDF do pedido' });
                                }
                            },
                        }] : []),
                        ...(isEditing && sale?.status === 'draft' && canSales ? [{
                            label: 'Aprovar',
                            icon: 'approve',
                            onClick: () => setApproveDialogOpen(true),
                        }] : []),
                        ...(isEditing && ['draft', 'approved', 'partial'].includes(sale?.status) && canSales ? [{
                            label: 'Cancelar',
                            icon: 'cancel',
                            variant: 'outline',
                            onClick: () => setCancelDialogOpen(true),
                        }] : []),
                        ...(isOpenForDelivery && canSales ? [{
                            label: 'Finalizar',
                            icon: 'finish',
                            variant: 'outline',
                            onClick: () => setFinishDialogOpen(true),
                        }] : []),
                    ]}
                    actions={[
                        { label: 'Voltar', icon: 'back', variant: 'outline', as: Link, to: '/sales' },
                        ...(canEdit && canSales ? [{
                            label: formLoading ? 'Salvando...' : 'Salvar',
                            icon: 'save',
                            onClick: handleSave,
                            disabled: formLoading || loading,
                        }] : []),
                    ]}
                />

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Status</p>
                                    <p className="text-2xl font-bold mt-1 text-gray-900">{sale ? (STATUS_LABELS[sale.status] || sale.status) : 'Orçado'}</p>
                                </div>
                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Valor Total</p>
                                    <p className="text-2xl font-bold mt-1 text-gray-900">{formatBRL(total)}</p>
                                </div>
                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Quantidade Pedida</p>
                                    <p className="text-2xl font-bold mt-1 text-gray-900">{formatQuantity(totalOrderedQty)}</p>
                                </div>
                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Progresso da Entrega</p>
                                    <p className="text-2xl font-bold mt-1 text-gray-900">{deliveryProgress.toFixed(0)}%</p>
                                </div>
                                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="general" activeValue={activeTab} onValueChange={setActiveTab}>
                            Geral
                        </TabsTrigger>
                        <TabsTrigger value="items" activeValue={activeTab} onValueChange={setActiveTab}>
                            Itens
                        </TabsTrigger>
                        {isEditing && (
                            <TabsTrigger value="deliveries" activeValue={activeTab} onValueChange={setActiveTab}>
                                Atendimento
                            </TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value="general" activeValue={activeTab} className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Dados do pedido</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <Autocomplete
                                                        options={clients}
                                                        getOptionLabel={(option) => option.name}
                                                        getOptionValue={(option) => option.id}
                                                        value={clientId}
                                                        onChange={setClientId}
                                                        placeholder="Buscar cliente..."
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                                {canEdit && (
                                                    <Button type="button" variant="outline" onClick={() => setClientModalOpen(true)}>
                                                        + Novo
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor</label>
                                            <Autocomplete
                                                options={users}
                                                getOptionLabel={(option) => option.name}
                                                getOptionValue={(option) => option.id}
                                                value={userId}
                                                onChange={setUserId}
                                                placeholder="Buscar vendedor..."
                                                disabled={!canEdit}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Ordem de compra do cliente</label>
                                            <Input
                                                value={customerPurchaseOrder}
                                                onChange={(e) => setCustomerPurchaseOrder(e.target.value)}
                                                maxLength={100}
                                                disabled={!canEdit}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Forma de pagamento</label>
                                            <Input value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} disabled={!canEdit} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Origem</label>
                                            <select
                                                className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
                                                value={origin}
                                                onChange={(e) => setOrigin(e.target.value)}
                                                disabled={!canEdit}
                                            >
                                                <option value="">Não informada</option>
                                                {ORIGIN_OPTIONS.map((o) => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Detalhes do pedido</label>
                                        <textarea
                                            value={details}
                                            onChange={(e) => setDetails(e.target.value)}
                                            rows={4}
                                            className="flex w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            disabled={!canEdit}
                                        />
                                    </div>

                                    {isEditing && sale && (
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Data de criação</label>
                                                <Input value={sale.created_at ? formatDateTimeBR(sale.created_at) : '-'} disabled readOnly />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Válido até</label>
                                                <Input value={sale.valid_until ? formatDateBR(sale.valid_until) : '-'} disabled readOnly />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                                <Input value={STATUS_LABELS[sale.status] || sale.status} disabled readOnly />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Criado por</label>
                                                <Input value={sale.user?.name || '-'} disabled readOnly />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="items" activeValue={activeTab} className="mt-6">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle>Itens</CardTitle>
                                    {canEdit && (
                                        <Button type="button" variant="outline" onClick={openItemDrawerForNew}>
                                            + Adicionar item
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent>
                                <TableComponent
                                    columns={itemColumns}
                                    data={items}
                                    onEdit={canEdit ? openItemDrawerForEdit : undefined}
                                    onDelete={canEdit ? (row) => setDeleteItemDialog({ open: true, id: row.id }) : undefined}
                                    emptyMessage="Nenhum item adicionado"
                                />

                                <div className="mt-6 flex justify-end">
                                    <div className="text-lg font-semibold text-gray-900">
                                        Total: {formatBRL(total)}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {isEditing && (
                        <TabsContent value="deliveries" activeValue={activeTab} className="mt-6">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Atendimentos</CardTitle>
                                        {isOpenForDelivery && canSales && (
                                            <Button onClick={openDeliveryDrawer}>
                                                Registrar Atendimento
                                            </Button>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {!['approved', 'partial', 'completed'].includes(sale?.status) ? (
                                        <p className="text-sm text-gray-500">O pedido precisa estar aprovado para registrar atendimentos.</p>
                                    ) : (
                                        <TableComponent
                                            columns={deliveryColumns}
                                            data={sale.deliveries || []}
                                            emptyMessage="Nenhum atendimento registrado"
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                </Tabs>
            </div>

            <ClientQuickCreateModal
                open={clientModalOpen}
                onClose={() => setClientModalOpen(false)}
                onCreated={(created) => {
                    setClients((prev) => [...prev, created]);
                    setClientId(created.id);
                }}
            />

            <ConfirmDialog
                open={approveDialogOpen}
                title="Aprovar pedido"
                message="Ao aprovar, o pedido passa a poder receber atendimentos (entregas). Não gera baixa de estoque imediatamente. Deseja continuar?"
                onConfirm={handleApprove}
                onCancel={() => setApproveDialogOpen(false)}
            />

            <ConfirmDialog
                open={cancelDialogOpen}
                title="Cancelar pedido"
                message="Tem certeza que deseja cancelar? Entregas já registradas não são estornadas, apenas o saldo pendente é encerrado."
                onConfirm={handleCancel}
                onCancel={() => setCancelDialogOpen(false)}
            />

            <ConfirmDialog
                open={finishDialogOpen}
                title="Finalizar pedido"
                message="Ao finalizar, o pedido é concluído mesmo com saldo pendente: o que não foi atendido deixa de reservar estoque e não será mais possível registrar atendimentos. Deseja continuar?"
                onConfirm={handleFinish}
                onCancel={() => setFinishDialogOpen(false)}
            />

            <ConfirmDialog
                open={deleteItemDialog.open}
                title="Remover item"
                message="Tem certeza que deseja remover este item do pedido?"
                onConfirm={handleRemoveItemConfirm}
                onCancel={() => setDeleteItemDialog({ open: false, id: null })}
            />

            <Drawer
                open={itemDrawerOpen}
                onClose={() => setItemDrawerOpen(false)}
                title={editingItem ? 'Editar item' : 'Adicionar item'}
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setItemDrawerOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="button" onClick={handleItemSave}>Salvar</Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Produto *</label>
                        <Autocomplete
                            options={products}
                            getOptionLabel={(option) => `${option.code} - ${option.description}`}
                            getOptionValue={(option) => option.id}
                            value={itemFormData.product_id}
                            onChange={handleItemProductSelect}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Depósito *</label>
                        <select
                            value={itemFormData.warehouse_id ?? ''}
                            onChange={handleItemWarehouseSelect}
                            className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            required
                        >
                            <option value="">Selecione...</option>
                            {warehouses.map((w) => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade *</label>
                        <Input
                            type="number"
                            step="0.001"
                            min="0"
                            name="quantity"
                            value={itemFormData.quantity}
                            onChange={handleItemFormChange}
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Valor unitário *</label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            name="unit_price"
                            value={itemFormData.unit_price}
                            onChange={handleItemFormChange}
                            required
                        />
                    </div>
                    {itemFormData.product_id && itemFormData.warehouse_id && (
                        <p className="text-xs text-gray-500">
                            Estoque disponível: {itemAvailabilityLoading ? 'carregando...' : (itemAvailability != null ? (
                                <span className={itemAvailability < 0 ? 'text-rose-600 font-medium' : 'font-medium'}>{formatQuantity(itemAvailability)}</span>
                            ) : '-')}
                        </p>
                    )}
                </div>
            </Drawer>

            <Drawer
                open={deliveryDrawerOpen}
                onClose={() => setDeliveryDrawerOpen(false)}
                title="Registrar atendimento"
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setDeliveryDrawerOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="button" onClick={handleDeliverySave} disabled={deliverySaving}>
                            {deliverySaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                        <MaskedInput mask="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} required />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Número da NF</label>
                        <Input value={deliveryInvoice} onChange={(e) => setDeliveryInvoice(e.target.value)} maxLength={30} />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Local de entrega *</label>
                        <select
                            value={deliveryLocation}
                            onChange={(e) => setDeliveryLocation(e.target.value)}
                            className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                            <option value="">Selecione</option>
                            {deliveryLocationOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        {deliveryLocationOptions.length === 0 && (
                            <p className="mt-1 text-xs text-rose-600">Cliente sem endereço e sem obras cadastradas</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                        <textarea
                            value={deliveryNotes}
                            onChange={(e) => setDeliveryNotes(e.target.value)}
                            rows={3}
                            className="flex w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                    </div>

                    <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Itens</p>
                        <TableComponent
                            columns={[
                                {
                                    key: 'product_id',
                                    label: 'Produto',
                                    render: (value) => {
                                        const product = productById.get(value);
                                        return product ? `${product.code} - ${product.description}` : `Item #${value}`;
                                    },
                                },
                                {
                                    key: 'remaining',
                                    label: 'Restante',
                                    align: 'right',
                                    render: (value) => formatQuantity(value),
                                },
                                {
                                    key: 'quantity',
                                    label: 'Qtd. a entregar',
                                    align: 'right',
                                    render: (value, row) => (
                                        <Input
                                            type="number"
                                            step="0.001"
                                            min="0"
                                            max={row.remaining}
                                            className="w-28 text-right ml-auto"
                                            value={deliveryQuantities[row.id] ?? ''}
                                            onChange={(e) => handleDeliveryQuantityChange(row.id, e.target.value)}
                                            placeholder="0"
                                        />
                                    ),
                                },
                            ]}
                            data={deliverableItems}
                            emptyMessage="Nenhum item pendente de atendimento"
                        />
                    </div>
                </div>
            </Drawer>
        </AppLayout>
    );
}

export default SaleForm;
